/**
 * Post-processing composite subsystem. (§6.1 pipeline tail + §6.2 tone mapping)
 *
 * This is the ONE CompositeSubsystem in the render layer (see context.ts): the
 * shell stops calling `gl.render` itself and hands us the whole frame through
 * `composite(ctx)`. Everything about the EffectComposer — its render targets,
 * its pass list, its lifetime — lives inside this file and nowhere else. The
 * shell knows only "someone composites"; the other subsystems know nothing about
 * post at all. That is the seam that lets six agents share renderer.ts without
 * colliding (§15.2).
 *
 * ── What we own vs. what lighting.ts owns ───────────────────────────────────
 * lighting.ts explicitly does NOT touch tone mapping and leaves it to us (see
 * its header). So THIS file owns `gl.toneMapping` and `gl.outputColorSpace`, and
 * it is the only place they are set. lighting.ts owns `gl.shadowMap.*`, the
 * lights, and `scene.environment/background`; we never touch those. The two
 * coordinate solely through that split — no shared mutable state, no ordering
 * hazard beyond "lighting inits first" (guaranteed by subsystems.ts order).
 *
 * ── The §6.1 stack, and where reality diverges from the brief (D9 honesty) ──
 * The brief's tail is:  SSAO → Bloom → MotionBlur → ACES Tonemap → Color-Grade
 * LUT → TAA → FXAA → Vignette, built on the pmndrs `postprocessing` composer.
 * We build on that exact library (it IS installed — v6.39.2; see the hand-off
 * note), and cover: SSAO, Bloom, ACES tone mapping, Vignette, and an SMAA
 * anti-alias pass. Three items in the brief have NO implementation in pmndrs v6
 * and are NOT faked here:
 *   - MotionBlur — pmndrs v6 ships no motion-blur effect (the old velocity-buffer
 *     pass was dropped). `quality.motionBlur` is read and, when it can never be
 *     honoured, left as a documented gap rather than a fake full-screen blur.
 *   - TAA — pmndrs v6 has no temporal AA. `quality.taa` instead gates the SMAA
 *     pass: SMAA is the honest spatial stand-in for the AA slot. Named as such,
 *     not smuggled in as "TAA".
 *   - Color-grade LUT — no LUT asset is shipped (§16 bans downloads and none is
 *     authored yet), so the LUT slot is intentionally empty. LUT3DEffect is
 *     available the day a .cube is authored; wiring it is a one-line add.
 * These gaps are surfaced in the hand-off report, per D9 ("never fake a seam —
 * document it").
 *
 * ── The Low-tier fast path (§6.8 — HARD requirement) ────────────────────────
 * When `quality.postEnabled === false` (the Low tier, targeting an Intel iGPU),
 * we take a NO-COMPOSER path: `composite()` calls plain `gl.render(scene, camera)`
 * and the EffectComposer is NEVER CONSTRUCTED. An idle composer still costs a
 * full-screen blit through its input/output buffers every frame — a cost the
 * 60fps iGPU floor cannot absorb. So on Low there are zero extra render targets,
 * zero extra draws, and ACES tone mapping is applied by the renderer itself
 * (three's built-in `ACESFilmicToneMapping`) so the image still grades correctly
 * without a post pass.
 *
 * ── Zero-alloc discipline (§15.7) ───────────────────────────────────────────
 * `update()` runs at 60Hz and allocates nothing — it only stashes the frame dt
 * (a `Vector2` scratch for sizing is a field). Allocation happens only at
 * init / setQuality / onResize, i.e. never on the steady-state hot path.
 */

import {
  ACESFilmicToneMapping,
  HalfFloatType,
  NoToneMapping,
  Source,
  SRGBColorSpace,
  Vector2,
  type DepthTexture,
} from 'three';
import {
  BloomEffect,
  EffectComposer,
  EffectPass,
  NormalPass,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  SSAOEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
  VignetteTechnique,
  type Effect,
  type Pass,
} from 'postprocessing';
import type {
  CompositeSubsystem,
  QualitySettings,
  RenderContext,
  RenderState,
} from './context.js';
import { IMPOSTOR_LAYER } from './context.js';

/**
 * Hide the impostor layer for the duration of the normal pass.
 *
 * Wrapping `render` rather than handing the pass its own camera is deliberate:
 * a second camera would have to be kept in sync with the scene camera's
 * position, rotation, projection and aspect on every resize and every frame,
 * and any missed field silently corrupts the normal buffer. Toggling two bits
 * on the real camera cannot drift, and it is restored in a `finally` so a throw
 * inside the pass cannot leave the grass invisible.
 */
function maskImpostorsDuringNormalPass(pass: NormalPass, camera: RenderContext['camera']): void {
  const inner = pass.render.bind(pass);
  pass.render = ((...args: Parameters<NormalPass['render']>) => {
    const restore = camera.layers.isEnabled(IMPOSTOR_LAYER);
    camera.layers.disable(IMPOSTOR_LAYER);
    try {
      inner(...args);
    } finally {
      if (restore) camera.layers.enable(IMPOSTOR_LAYER);
    }
  }) as NormalPass['render'];
}

// ---------------------------------------------------------------------------
// Post-local tunables — cosmetic, never touched by the sim. (§6.1)
// ---------------------------------------------------------------------------

/** Bloom threshold per §6.1 ("Bloom (threshold 1.0)"). Broadcast bloom is a
 *  gentle bright-highlight halo, not a glow filter — modest intensity/radius. */
const BLOOM_LUMINANCE_THRESHOLD = 1.0;
const BLOOM_LUMINANCE_SMOOTHING = 0.05;
const BLOOM_INTENSITY = 0.45; // 0.6 amplified night+wet specular into glare
const BLOOM_RADIUS = 0.62;

/** SSAO tuned for pitch scale (metres): a broad contact-shadow under players
 *  and around the goal frames, never a gritty crevice-darkening filter. */
const SSAO_INTENSITY = 1.6;
const SSAO_RADIUS = 0.18;
const SSAO_WORLD_DISTANCE_THRESHOLD = 24;
const SSAO_WORLD_DISTANCE_FALLOFF = 6;
const SSAO_WORLD_PROXIMITY_THRESHOLD = 6;
const SSAO_WORLD_PROXIMITY_FALLOFF = 3;

/** SSAO sample budget scales with tier — high uses fewer rings/samples than
 *  ultra so the iGPU-adjacent "high on a laptop" case keeps its headroom. */
const SSAO_SAMPLES: Record<QualitySettings['tier'], number> = {
  low: 0, // never built on Low (no post at all)
  medium: 0, // SSAO is a high+ feature per QUALITY_PRESETS
  high: 9,
  ultra: 16,
};

/** Vignette: a whisper of edge darkening for the broadcast look (D0). */
const VIGNETTE_OFFSET = 0.32;
const VIGNETTE_DARKNESS = 0.42;

/** SMAA preset by tier (only ever built when post is on AND taa is set). */
const SMAA_PRESET: Record<QualitySettings['tier'], SMAAPreset> = {
  low: SMAAPreset.LOW,
  medium: SMAAPreset.MEDIUM,
  high: SMAAPreset.HIGH,
  ultra: SMAAPreset.ULTRA,
};

/** MSAA sample count on the composer's HDR buffer, per tier. This is the
 *  baseline AA when SMAA is not active (e.g. Medium, where `taa` is false) and
 *  is free-ish on WebGL2. Zero on tiers that never build a composer.
 *
 *  NOT free on a HalfFloat buffer at a high pixel ratio, which is what High and
 *  Ultra actually are: 4 samples of RGBA16F at 3840x2160 is a ~265 MB target
 *  plus a per-frame resolve. Measured on an RX 9070 XT at 3840x2160, dropping
 *  High from 4 to 0 took the frame from 6.68 ms to 4.45 ms — 2.23 ms, a third
 *  of the entire frame. Nothing was lost visually, because High and Ultra set
 *  `taa: true`, so the SMAA pass below is already doing the antialiasing and
 *  MSAA was the second of two AA solutions running at once. Medium keeps 2
 *  samples precisely because it has `taa: false` and therefore no SMAA pass —
 *  that is the case the original comment describes and it is still correct.
 */
const COMPOSER_MSAA: Record<QualitySettings['tier'], number> = {
  low: 0,
  medium: 2,
  high: 0,
  ultra: 0,
};

class PostPipeline implements CompositeSubsystem {
  readonly name = 'post';

  private ctx!: RenderContext;

  /** Null whenever post is disabled (Low tier) — the fast path never builds it. */
  private composer: EffectComposer | null = null;
  /** Passes we own, kept for disposal. The composer disposes its own targets on
   *  dispose(), but effects/passes we constructed are disposed explicitly. */
  private renderPass: RenderPass | null = null;
  private normalPass: NormalPass | null = null;
  private effectPass: EffectPass | null = null;
  private smaaPass: EffectPass | null = null;
  /** Effects held for disposal (EffectPass.dispose does not free member effects). */
  private effects: Effect[] = [];

  /** Latest frame dt, stashed in update() and fed to composer.render() so the
   *  composer's timer matches the sim-driven frame cadence. */
  private frameDt = 1 / 60;

  // ── Preallocated scratch — no allocation on the 60Hz path (§15.7).
  private readonly _size = new Vector2();

  init(ctx: RenderContext): void {
    this.ctx = ctx;
    // We own the renderer's colour management + tone mapping (lighting.ts leaves
    // it to us). Output is always sRGB; tone-map ownership flips with the path.
    ctx.gl.outputColorSpace = SRGBColorSpace;
    this.applyForQuality(ctx.quality);
  }

  update(state: RenderState, _ctx: RenderContext): void {
    // Composite subsystems still get an update() before the shell calls
    // composite(). All we need from the frame is dt for the composer's timer;
    // everything visual is decided by the scene the other subsystems built.
    this.frameDt = state.dt;
  }

  /**
   * The composite. Two mutually-exclusive paths:
   *   - post ON  → drive the EffectComposer (RenderPass + effects + AA).
   *   - post OFF → plain gl.render, composer never constructed (§6.8 fast path).
   */
  composite(ctx: RenderContext): void {
    if (this.composer) {
      this.composer.render(this.frameDt);
    } else {
      ctx.gl.render(ctx.scene, ctx.camera);
    }
  }

  setQuality(quality: QualitySettings, ctx: RenderContext): void {
    this.ctx = ctx;
    this.applyForQuality(quality);
  }

  onResize(ctx: RenderContext): void {
    if (!this.composer) return;
    // The composer sizes its render targets in CSS pixels and applies the
    // renderer's pixel ratio internally — mirror exactly what renderer.setSize
    // was given rather than the device-pixel viewport figures on ctx.
    ctx.gl.getSize(this._size);
    this.composer.setSize(this._size.x, this._size.y);
  }

  dispose(): void {
    this.teardownComposer();
  }

  // ── Build / rebuild ────────────────────────────────────────────────────────

  /**
   * Bring the pipeline into agreement with a quality tier. Rebuilds the composer
   * from scratch on any change — tier switches are rare (a settings action), so
   * a clean teardown+build is simpler and safer than surgically toggling passes,
   * and it guarantees no stale render target survives a resolution/feature shift.
   */
  private applyForQuality(quality: QualitySettings): void {
    this.teardownComposer();

    if (!quality.postEnabled) {
      // Low tier: no composer at all. Apply ACES on the renderer itself so the
      // plain gl.render path still grades to the broadcast look (§6.2/D0).
      this.ctx.gl.toneMapping = ACESFilmicToneMapping;
      return;
    }

    // Composer path: the ToneMappingEffect applies ACES, so the renderer must NOT
    // double tone-map. (The composer converts to sRGB in its final pass.)
    this.ctx.gl.toneMapping = NoToneMapping;
    this.buildComposer(quality);
  }

  private buildComposer(quality: QualitySettings): void {
    const gl = this.ctx.gl;
    const scene = this.ctx.scene;
    const camera = this.ctx.camera;

    // HalfFloat frame buffers so bloom + tone mapping work in HDR without banding.
    this.composer = new EffectComposer(gl, {
      frameBufferType: HalfFloatType,
      multisampling: COMPOSER_MSAA[quality.tier],
    });

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Ordered per §6.1: SSAO → Bloom → (MotionBlur gap) → ACES → (LUT gap) →
    // (TAA→SMAA, a separate pass) → Vignette. SSAO, Bloom, ACES and Vignette
    // merge into one EffectPass; SMAA runs in its own pass afterwards.
    const chain: Effect[] = [];

    // ── SSAO (high+). Needs a normal buffer; add a NormalPass that renders the
    //    scene's normals into its own target (never to screen). Depth comes from
    //    the composer's depth buffer, wired to the effect automatically.
    if (quality.ssao) {
      this.normalPass = new NormalPass(scene, camera);
      // NormalPass renders the WHOLE scene a second time through
      // `scene.overrideMaterial = MeshNormalMaterial` (postprocessing's
      // OverrideMaterialManager). That substitution keeps each mesh's geometry
      // and instance count but discards its custom vertex shader — and the
      // grass, clumps, crowd and flags place every instance IN that shader from
      // per-instance attributes a normal material never declares. The result is
      // ~190,000 unit quads stacked at the world origin, i.e. dead centre of the
      // pitch, shading a screenful of overdraw for a buffer that then samples it
      // as garbage. Measured as +39 draw calls and +592,842 triangles per frame,
      // and it is the difference between "unmeasurably slow" and 1.4 fps on a
      // software rasteriser.
      //
      // Masking the layer off for the duration of the pass is both the fix and
      // the correct result: these are unlit camera-facing billboards, so their
      // true contribution to an ambient-occlusion normal buffer is nil.
      maskImpostorsDuringNormalPass(this.normalPass, camera);
      this.composer.addPass(this.normalPass);
      const ssao = new SSAOEffect(camera, this.normalPass.texture, {
        samples: SSAO_SAMPLES[quality.tier] || 9,
        rings: 4,
        radius: SSAO_RADIUS,
        intensity: SSAO_INTENSITY,
        luminanceInfluence: 0.7,
        worldDistanceThreshold: SSAO_WORLD_DISTANCE_THRESHOLD,
        worldDistanceFalloff: SSAO_WORLD_DISTANCE_FALLOFF,
        worldProximityThreshold: SSAO_WORLD_PROXIMITY_THRESHOLD,
        worldProximityFalloff: SSAO_WORLD_PROXIMITY_FALLOFF,
        fade: 0.02,
      });
      chain.push(ssao);
    }

    // ── Bloom (medium+). Mipmap blur = the cheap, wide, broadcast-soft glow.
    if (quality.bloom) {
      const bloom = new BloomEffect({
        luminanceThreshold: BLOOM_LUMINANCE_THRESHOLD,
        luminanceSmoothing: BLOOM_LUMINANCE_SMOOTHING,
        intensity: BLOOM_INTENSITY,
        radius: BLOOM_RADIUS,
        mipmapBlur: true,
      });
      chain.push(bloom);
    }

    // NOTE: MotionBlur would sit here (§6.1). pmndrs v6 has no motion-blur effect;
    // `quality.motionBlur` (${...}) is a documented gap, not faked. See header.
    void quality.motionBlur;

    // ── ACES filmic tone mapping (§6.1/§6.2). Always present on the post path.
    const tonemap = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    chain.push(tonemap);

    // NOTE: Color-grade LUT would sit here (§6.1) — no .cube shipped yet (§16).

    // ── Vignette last in the colour pass (a broadcast edge-darken, D0).
    const vignette = new VignetteEffect({
      technique: VignetteTechnique.DEFAULT,
      offset: VIGNETTE_OFFSET,
      darkness: VIGNETTE_DARKNESS,
    });
    chain.push(vignette);

    this.effects = chain;
    this.effectPass = new EffectPass(camera, ...chain);
    this.composer.addPass(this.effectPass);

    // ── AA slot (§6.1 TAA→FXAA). pmndrs v6 has no TAA; `quality.taa` gates SMAA
    //    as the honest spatial stand-in. When taa is off, the composer's MSAA
    //    (COMPOSER_MSAA) is the baseline AA.
    if (quality.taa) {
      const smaa = new SMAAEffect({ preset: SMAA_PRESET[quality.tier] });
      this.effects.push(smaa);
      this.smaaPass = new EffectPass(camera, smaa);
      this.composer.addPass(this.smaaPass);
    }

    // Size the freshly-built composer to the current viewport.
    gl.getSize(this._size);
    this.composer.setSize(this._size.x, this._size.y);

    this.splitDepthTextureSources();
  }

  /**
   * Upstream bug workaround (postprocessing ≤6.39.3 with three ≥r151): when a
   * pass needs scene depth, the composer creates ONE DepthTexture and `.clone()`s
   * it for the input buffer, output buffer and its "StableDepth" target. But
   * three keys GL textures on `Texture.source`, which `.clone()` SHARES — so all
   * three depth attachments resolve to the SAME GL image, and the library's
   * depth blits (input→stable) become self-blits: GL_INVALID_OPERATION
   * "read and write depth stencil attachments cannot be the same image" spammed
   * every frame on ANGLE/Windows (observed live on this machine), and the stable
   * depth copy silently never happens. Giving each clone its own Source restores
   * distinct GL images, which is exactly what the library's design assumes.
   */
  private splitDepthTextureSources(): void {
    const c = this.composer as unknown as {
      inputBuffer?: { depthTexture?: DepthTexture | null };
      outputBuffer?: { depthTexture?: DepthTexture | null };
      depthRenderTarget?: { depthTexture?: DepthTexture | null } | null;
    } | null;
    if (!c) return;
    const seen = new Set<unknown>();
    for (const t of [
      c.inputBuffer?.depthTexture,
      c.outputBuffer?.depthTexture,
      c.depthRenderTarget?.depthTexture,
    ]) {
      if (!t) continue;
      if (seen.has(t.source)) {
        t.source = new Source(t.image);
        t.needsUpdate = true;
      }
      seen.add(t.source);
    }
  }

  private teardownComposer(): void {
    // Free effects first (EffectPass.dispose does not free its member effects),
    // then passes, then the composer (which disposes its own render targets).
    for (const e of this.effects) e.dispose?.();
    this.effects = [];
    this.effectPass?.dispose();
    this.smaaPass?.dispose();
    this.normalPass?.dispose();
    this.renderPass?.dispose();
    this.composer?.dispose();
    this.effectPass = null;
    this.smaaPass = null;
    this.normalPass = null;
    this.renderPass = null;
    this.composer = null;
  }
}

export function createPostPipeline(): CompositeSubsystem {
  return new PostPipeline();
}

// Keep `Pass` a meaningful import for readers: every entry the composer holds
// (RenderPass, NormalPass, EffectPass) is a `Pass`, disposed in teardown above.
export type { Pass };
